import { create } from "zustand";
import type { HomeSectionId } from "../content/siteContent";
import { projects } from "../data/projects";

export type ExperienceMode = "home" | "detail";
export type NavigationIntentSource = "nav" | "hash";

export type NavigationIntent = {
  source: NavigationIntentSource;
  target: HomeSectionId;
};

type PointerState = {
  x: number;
  y: number;
};

type AudioSettings = {
  enabled: boolean;
  masterVolume: number;
  musicVolume: number;
  sfxVolume: number;
  ambientVolume: number;
};

const defaultAudioSettings: AudioSettings = {
  enabled: false,
  masterVolume: 0.65,
  musicVolume: 0.48,
  sfxVolume: 0.72,
  ambientVolume: 0.28
};

const audioSettingsStorageKey = "sevenstones.audio.v1";

function readStoredAudioSettings(): AudioSettings {
  if (typeof window === "undefined") {
    return defaultAudioSettings;
  }

  try {
    const storedValue = window.localStorage.getItem(audioSettingsStorageKey);
    if (!storedValue) {
      return defaultAudioSettings;
    }

    const parsedValue = JSON.parse(storedValue) as Partial<AudioSettings>;
    return {
      enabled: parsedValue.enabled ?? defaultAudioSettings.enabled,
      masterVolume: clamp01(parsedValue.masterVolume ?? defaultAudioSettings.masterVolume),
      musicVolume: clamp01(parsedValue.musicVolume ?? defaultAudioSettings.musicVolume),
      sfxVolume: clamp01(parsedValue.sfxVolume ?? defaultAudioSettings.sfxVolume),
      ambientVolume: clamp01(parsedValue.ambientVolume ?? defaultAudioSettings.ambientVolume)
    };
  } catch {
    return defaultAudioSettings;
  }
}

function persistAudioSettings(settings: AudioSettings) {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.setItem(audioSettingsStorageKey, JSON.stringify(settings));
  } catch {
    // Audio preferences are non-critical; private browsing can reject storage writes.
  }
}

const maxProjectIndex = Math.max(projects.length - 1, 0);

function clamp01(value: number) {
  return Math.min(Math.max(value, 0), 1);
}

function clampProjectProgress(progress: number) {
  return Math.min(Math.max(progress, 0), maxProjectIndex);
}

type AppStore = {
  currentSection: HomeSectionId;
  targetSection: HomeSectionId;
  scrollVelocity: number;
  transitionProgress: number;
  isTransitioning: boolean;
  hasEntered: boolean;
  reducedMotion: boolean;
  assetsReady: boolean;
  navOpen: boolean;
  audioEnabled: boolean;
  audioUnlocked: boolean;
  masterVolume: number;
  musicVolume: number;
  sfxVolume: number;
  ambientVolume: number;
  navigationIntent: NavigationIntent | null;
  experienceMode: ExperienceMode;
  activeProjectIndex: number;
  projectCarouselProgress: number;
  activeGameSlug: string | null;
  studioHovered: boolean;
  teamHovered: boolean;
  teamPointer: PointerState;
  setCurrentSection: (section: HomeSectionId) => void;
  setTargetSection: (section: HomeSectionId) => void;
  setScrollVelocity: (velocity: number) => void;
  setTransitionProgress: (progress: number) => void;
  completeTransition: () => void;
  setReducedMotion: (reducedMotion: boolean) => void;
  setAssetsReady: (ready: boolean) => void;
  setNavOpen: (open: boolean) => void;
  setAudioEnabled: (enabled: boolean) => void;
  setAudioUnlocked: (unlocked: boolean) => void;
  setMasterVolume: (volume: number) => void;
  setMusicVolume: (volume: number) => void;
  setSfxVolume: (volume: number) => void;
  setAmbientVolume: (volume: number) => void;
  setNavigationIntent: (intent: NavigationIntent | null) => void;
  clearNavigationIntent: () => void;
  setExperienceMode: (mode: ExperienceMode) => void;
  setHasEntered: (entered: boolean) => void;
  setActiveProjectIndex: (index: number) => void;
  setProjectCarouselProgress: (progress: number) => void;
  setActiveGameSlug: (slug: string | null) => void;
  setStudioHovered: (hovered: boolean) => void;
  setTeamHovered: (hovered: boolean) => void;
  setTeamPointer: (pointer: PointerState) => void;
};

const storedAudioSettings = readStoredAudioSettings();

export const useAppStore = create<AppStore>((set) => ({
  currentSection: "studio",
  targetSection: "studio",
  scrollVelocity: 0,
  transitionProgress: 1,
  isTransitioning: false,
  hasEntered: false,
  reducedMotion: false,
  assetsReady: false,
  navOpen: false,
  audioEnabled: storedAudioSettings.enabled,
  audioUnlocked: false,
  masterVolume: storedAudioSettings.masterVolume,
  musicVolume: storedAudioSettings.musicVolume,
  sfxVolume: storedAudioSettings.sfxVolume,
  ambientVolume: storedAudioSettings.ambientVolume,
  navigationIntent: null,
  experienceMode: "home",
  activeProjectIndex: 0,
  projectCarouselProgress: 0,
  activeGameSlug: null,
  studioHovered: false,
  teamHovered: false,
  teamPointer: { x: 0, y: 0 },
  setCurrentSection: (section) => set({ currentSection: section }),
  setScrollVelocity: (scrollVelocity) => set({ scrollVelocity }),
  setTargetSection: (section) =>
    set((state) => {
      if (state.isTransitioning || state.targetSection === section || state.currentSection === section) {
        return state;
      }

      return {
        targetSection: section,
        isTransitioning: true,
        transitionProgress: 0
      };
    }),
  setTransitionProgress: (progress) => set({ transitionProgress: progress }),
  completeTransition: () =>
    set((state) => ({
      currentSection: state.targetSection,
      transitionProgress: 1,
      isTransitioning: false
    })),
  setReducedMotion: (reducedMotion) => set({ reducedMotion }),
  setAssetsReady: (assetsReady) => set({ assetsReady }),
  setNavOpen: (navOpen) => set({ navOpen }),
  setAudioEnabled: (audioEnabled) =>
    set((state) => {
      persistAudioSettings({
        enabled: audioEnabled,
        masterVolume: state.masterVolume,
        musicVolume: state.musicVolume,
        sfxVolume: state.sfxVolume,
        ambientVolume: state.ambientVolume
      });
      return { audioEnabled };
    }),
  setAudioUnlocked: (audioUnlocked) => set({ audioUnlocked }),
  setMasterVolume: (masterVolume) =>
    set((state) => {
      const nextVolume = clamp01(masterVolume);
      persistAudioSettings({
        enabled: state.audioEnabled,
        masterVolume: nextVolume,
        musicVolume: state.musicVolume,
        sfxVolume: state.sfxVolume,
        ambientVolume: state.ambientVolume
      });
      return { masterVolume: nextVolume };
    }),
  setMusicVolume: (musicVolume) =>
    set((state) => {
      const nextVolume = clamp01(musicVolume);
      persistAudioSettings({
        enabled: state.audioEnabled,
        masterVolume: state.masterVolume,
        musicVolume: nextVolume,
        sfxVolume: state.sfxVolume,
        ambientVolume: state.ambientVolume
      });
      return { musicVolume: nextVolume };
    }),
  setSfxVolume: (sfxVolume) =>
    set((state) => {
      const nextVolume = clamp01(sfxVolume);
      persistAudioSettings({
        enabled: state.audioEnabled,
        masterVolume: state.masterVolume,
        musicVolume: state.musicVolume,
        sfxVolume: nextVolume,
        ambientVolume: state.ambientVolume
      });
      return { sfxVolume: nextVolume };
    }),
  setAmbientVolume: (ambientVolume) =>
    set((state) => {
      const nextVolume = clamp01(ambientVolume);
      persistAudioSettings({
        enabled: state.audioEnabled,
        masterVolume: state.masterVolume,
        musicVolume: state.musicVolume,
        sfxVolume: state.sfxVolume,
        ambientVolume: nextVolume
      });
      return { ambientVolume: nextVolume };
    }),
  setNavigationIntent: (navigationIntent) => set({ navigationIntent }),
  clearNavigationIntent: () => set({ navigationIntent: null }),
  setExperienceMode: (experienceMode) => set({ experienceMode }),
  setHasEntered: (hasEntered) => set({ hasEntered }),
  setActiveProjectIndex: (activeProjectIndex) => {
    const clampedIndex = clampProjectProgress(activeProjectIndex);
    set({
      activeProjectIndex: Math.round(clampedIndex),
      projectCarouselProgress: clampedIndex
    });
  },
  setProjectCarouselProgress: (projectCarouselProgress) =>
    set({
      projectCarouselProgress: clampProjectProgress(projectCarouselProgress)
    }),
  setActiveGameSlug: (activeGameSlug) => set({ activeGameSlug }),
  setStudioHovered: (studioHovered) => set({ studioHovered }),
  setTeamHovered: (teamHovered) => set({ teamHovered }),
  setTeamPointer: (teamPointer) => set({ teamPointer })
}));
